import React, {
  MouseEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'

import { Option } from 'components_new/molecules/SelectSearch'

import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import Menu from 'components_new/atoms/Menu'
import MenuItem from 'components_new/atoms/Menu/MenuItem'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SelectButtonBase, {
  SelectButtonBaseProps
} from 'components_new/molecules/SelectButtonBase'

import { getSelectedOption, getSelectedOptions } from 'utils/functions'

export type SelectButtonProps = SelectButtonBaseProps & {
  MenuProps?: { title?: string }
  onChange: (value: string) => void
  options: Option[]
  showActive?: boolean
}

const SelectButton = React.forwardRef<HTMLDivElement, SelectButtonProps>(
  (props, ref) => {
    const { MenuProps, onChange, options, showActive, value, ...ButtonProps } =
      props

    const buttonRef = useRef<HTMLDivElement | null>(null)

    useImperativeHandle(ref, () => buttonRef.current as HTMLDivElement)

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const focused = Boolean(anchorEl)

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
      setAnchorEl(null)
    }

    const selectedOption = useMemo((): {
      avatar: Option['avatar']
      title: string
    } | null => {
      let avatar: Option['avatar'] = undefined
      let title = ''

      if (Array.isArray(value)) {
        const selected = getSelectedOptions(
          value,
          options as (Omit<Option, 'id'> & { id: string })[]
        )

        title =
          selected.length > 0 ? selected.map((o) => o.title).join(', ') : ''
      } else {
        const selected =
          getSelectedOption(
            value,
            options as (Omit<Option, 'id'> & { id: string })[]
          ) ?? null

        avatar = selected?.avatar
        title = selected?.title ?? ''
      }

      if (title)
        return {
          avatar,
          title
        }

      return null
    }, [value, options])

    return (
      <>
        <SelectButtonBase
          active={showActive && selectedOption !== null}
          avatar={
            selectedOption?.avatar
              ? { title: '', variant: selectedOption.avatar }
              : undefined
          }
          focused={focused}
          onClick={handleClick}
          ref={buttonRef}
          value={selectedOption ? selectedOption.title : null}
          {...ButtonProps}
        />

        <Menu
          anchorEl={buttonRef.current}
          onClose={handleClose}
          onMouseDown={(event) => event.stopPropagation()}
          open={focused}
        >
          {MenuProps?.title ? (
            <>
              <SectionHeader title={MenuProps?.title} sx={{ px: 2 }} />
              <Divider sx={{ my: 1 }} />
            </>
          ) : null}
          {options.map((option) => {
            return (
              <MenuItem
                key={option.id}
                onClick={() => {
                  onChange(option.id as string)
                  handleClose()
                }}
                value={option.id}
              >
                <GenericListItem
                  disablePadding={true}
                  icon={option.icon ? { name: option.icon } : undefined}
                  title={option.title}
                  variant={option.avatar}
                  actions={
                    <Icon
                      fontSize="small"
                      name="Check"
                      sx={{ ml: 2, opacity: value === option.id ? 1 : 0 }}
                    />
                  }
                />
              </MenuItem>
            )
          })}
        </Menu>
      </>
    )
  }
)

SelectButton.displayName = 'SelectButton'

export default SelectButton
